import {Injectable} from '@angular/core'
import {ConnectionStatus, Network} from '@capacitor/network'
import {Observable, from, of} from 'rxjs'
import {combineLatestWith, map, startWith, switchMap, tap} from 'rxjs/operators'
import {ApiService} from '@uve-solutions/api-angular-client'
import {Translation, TranslocoLoader} from '@uve-solutions/composition/translate'
import {PreferencesStorageIds} from '@common/global-constants'
import {PreferencesApiService} from './storage/preferences-api.service'
import {ApiCacheResponse, StorageApiService} from './storage/storage-api.service'

export const PREFIX_KEY_CACHE_TRANSLATIONS = 'translation-street'

const MOCK_TRANSLATIONS_RESPONSE: ApiCacheResponse<Translation> = {
  createdAt: null,
  expiredAt: null,
  data: null,
}

/**
 * Loader personalizado de Transloco para la app Street.
 *
 * @description
 * Implementa `TranslocoLoader` para cargar traducciones combinando:
 *
 * - El bundle del idioma desde `@uve-solutions/translations` (fallback a
 *   inglés si el idioma no existe).
 * - Los `CustomLiterals` del backoffice por organización, obtenidos via
 *   `ApiService.organization().getOrganizationParam('CustomLiterals')`, para
 *   permitir que cada cliente sobreescriba literales concretos.
 *
 * Cachea el resultado combinado en `StorageApiService` con una clave por
 * `(userId, organizationId, lang)`, de modo que offline siempre hay
 * traducciones disponibles.
 *
 * Además, transforma recursivamente valores con prefijo `@:` (aliases en
 * estilo vue-i18n) a interpolaciones `{{...}}` compatibles con Transloco.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * Se registra como `TranslocoLoader` en el provider de Transloco; no se
 * inyecta normalmente desde componentes.
 *
 * ### Best Practices
 *
 * - No invocar `getTranslation` manualmente desde componentes; usar
 *   `TranslocoService` (o `TranslationServicex`).
 *
 * ### Bad Uses / Errores comunes
 *
 * - Modificar la clave `PREFIX_KEY_CACHE_TRANSLATIONS` sin vaciar el
 *   storage invalida las cachés de los usuarios.
 */
@Injectable({providedIn: 'root'})
export class StreetTranslocoService implements TranslocoLoader {
  constructor(
    private readonly api: ApiService,
    private readonly preferencesApiService: PreferencesApiService,
    private readonly storageApiService: StorageApiService
  ) {}

  /**
   * Contrato de `TranslocoLoader`: devuelve las traducciones para el idioma
   * `lang` combinando cache de storage y refresco online.
   */
  getTranslation = (lang: string): Observable<Translation> => {
    return this.getTranslationStorageData(lang).pipe(
      map(res => res ?? MOCK_TRANSLATIONS_RESPONSE),
      map(res => res.data)
    )
  }

  private readonly getLanguage = (lang: string): Promise<any> => {
    switch (lang) {
      case 'es':
        return import('node_modules/@uve-solutions/translations/src/i18n/es/es.json')
      case 'ca':
        return import('node_modules/@uve-solutions/translations/src/i18n/ca/ca.json')
      case 'de':
        return import('node_modules/@uve-solutions/translations/src/i18n/de/de.json')
      case 'fr':
        return import('node_modules/@uve-solutions/translations/src/i18n/fr/fr.json')
      case 'it':
        return import('node_modules/@uve-solutions/translations/src/i18n/it/it.json')
      case 'pt':
        return import('node_modules/@uve-solutions/translations/src/i18n/pt/pt.json')
      default:
        return import('node_modules/@uve-solutions/translations/src/i18n/en/en.json')
    }
  }

  private readonly getTranslationStorageData = (lang: string): Observable<ApiCacheResponse<Translation>> => {
    let online = true

    return from(Network.getStatus()).pipe(
      switchMap((connectionStatus: ConnectionStatus) => {
        online = connectionStatus.connected

        return from(this.getKeyCacheTranslations(lang))
      }),
      switchMap(keyCache => this.storageApiService.get<Translation[]>(keyCache)),
      switchMap(result => {
        if (online) {
          return this.callTranslationDataApiAndCache(lang)
        }

        return of(result)
      })
    )
  }

  private readonly callTranslationDataApiAndCache = (lang: string): Observable<ApiCacheResponse<Translation>> => {
    const global: Promise<Translation> = this.getLanguage(lang).then(x => {
      return x.default
    })

    const accountTranslations: Observable<Translation> = this.api
      .getApi()
      .organization()
      .getOrganizationParam('CustomLiterals')
      .pipe(
        map(el => {
          if (!el?.value) return {}

          let customLiterals = JSON.parse(el.value)

          customLiterals = customLiterals.reduce(
            (acc: Translation, curr: any) => ({...acc, ...curr}),
            {} as Translation
          )
          return customLiterals
        })
      )

    return accountTranslations.pipe(
      startWith(null),
      combineLatestWith(global),
      map(([accountTranslations, global]) => {
        return {
          ...global,
          ...(accountTranslations || {}),
        }
      }),
      map(translations => {
        return transformValuesRecursively(translations, value => {
          if (value.startsWith('@:')) {
            return '{{' + value.slice(2) + '}}'
          }
          return value
        })
      }),
      tap(res => {
        if (res) {
          this.getKeyCacheTranslations(lang).then(keyCache => {
            this.storageApiService.set(keyCache, res)
          })
        }
      }),
      map(res => ({
        createdAt: new Date().getTime(),
        expiredAt: null,
        data: res,
      }))
    )
  }

  private readonly getKeyCacheTranslations = async (lang: string): Promise<string> => {
    const {userId, organizationId} = await this.preferencesApiService.get<any>(
      PreferencesStorageIds.UVE_CURRENT_SESSION
    )

    const params: Record<string, any> = {
      userId,
      organizationId,
      lang,
    }

    const queryString = new URLSearchParams(params).toString()

    return `${PREFIX_KEY_CACHE_TRANSLATIONS}?${queryString}`
  }
}

// Utility function that recursively transform the string values of an object.
const transformValuesRecursively = (object: any, tfunction: (value: string) => string) => {
  const newObject = {} as any
  Object.entries(object).forEach(([key, value]) => {
    if (typeof value === 'string') {
      newObject[key] = tfunction(value)
    } else {
      newObject[key] = transformValuesRecursively(value, tfunction)
    }
  })
  return newObject
}
